const RETRYABLE_MARKET_STATUSES = new Set([
  'timeout',
  'partial_timeout',
  'failed',
  'partial_failed',
  'provider_unavailable',
  'not_configured',
  'unavailable',
]);

function text(value) {
  return String(value ?? '').trim();
}

function searchTermsText(searchTerms = {}) {
  return text(searchTerms?.searchTermsEn || searchTerms?.searchTerms || searchTerms?.text);
}

export function resolveSearchTeamRetryAvailability({ searchTerms, generationMeta } = {}) {
  const metadata = searchTerms?.metadata || {};
  const expansion = metadata.marketExpansion || searchTerms?.audit?.marketExpansion || {};
  const marketStatus = text(expansion.status).toLowerCase();
  const retryable = metadata.marketExpansionRetryable === true
    || metadata.retryable === true
    || expansion.retryable === true
    || RETRYABLE_MARKET_STATUSES.has(marketStatus);
  const insufficient = metadata.insufficientKeywordInventory === true;
  const mergeMeta = generationMeta?.mergeMeta || {};
  const taskId = text(mergeMeta.generationTaskId);
  const hasServerContext = mergeMeta.searchTeamRetryContext?.schemaVersion
    === 'listing_search_team_retry_context.v1';
  return {
    visible: Boolean((retryable || insufficient) && taskId && hasServerContext),
    retryable,
    insufficient,
    taskId,
    message: retryable
      ? '市场补词暂未完成，当前安全 Search Terms 已保留。'
      : insufficient
        ? '当前安全后台词较少，可以单独再跑一次市场补词。'
        : '',
  };
}

function scopeMismatch(message) {
  return Object.assign(new Error(message), {
    code: 'LISTING_SEARCH_TEAM_SCOPE_MISMATCH',
    status: 'scope_mismatch',
  });
}

/** Apply only the code-owned Search Terms field from a scoped retry response. */
export function applySearchTeamRetryResult(response, {
  jobId,
  taskId,
  getState,
  setStateBulk,
  scheduleAutoSave,
} = {}) {
  const currentJobId = text(getState?.('batchJobId'));
  const currentMeta = getState?.('listingGenerationMeta') || {};
  const currentTaskId = text(currentMeta?.mergeMeta?.generationTaskId);
  if (
    currentJobId !== text(jobId)
    || currentTaskId !== text(taskId)
    || text(response?.batchJobId) !== text(jobId)
    || text(response?.taskId) !== text(taskId)
  ) {
    throw scopeMismatch('补词结果与当前商品任务不匹配，已停止写回。');
  }

  const result = response?.result && typeof response.result === 'object' ? response.result : {};
  const incomingSearchTerms = response?.searchTerms
    || result?.sections?.searchTerms?.data
    || result?.searchTerms
    || null;
  const currentSearchTerms = getState?.('listingDraftSearchTerms');
  const nextSearchTerms = searchTermsText(incomingSearchTerms)
    ? incomingSearchTerms
    : currentSearchTerms;
  const previousText = searchTermsText(currentSearchTerms);
  const nextText = searchTermsText(nextSearchTerms);
  const textChanged = Boolean(nextText && nextText !== previousText);
  const currentV1 = getState?.('listingDraftV1');
  const resultMergeMeta = result?.mergeMeta && typeof result.mergeMeta === 'object'
    ? result.mergeMeta
    : currentMeta.mergeMeta;
  const nextMeta = {
    ...currentMeta,
    ...(result?.schemaVersion ? { schemaVersion: result.schemaVersion } : {}),
    ...(result?.status ? { status: result.status } : {}),
    ...(result?.readiness ? { readiness: result.readiness } : {}),
    ...(result?.quality ? { quality: result.quality } : {}),
    ...(result?.sections ? { sections: result.sections } : {}),
    mergeMeta: resultMergeMeta,
  };
  const patch = {
    listingDraftSearchTerms: nextSearchTerms,
    listingGenerationMeta: nextMeta,
    listingDraftV1: currentV1 && typeof currentV1 === 'object'
      ? { ...currentV1, searchTerms: nextSearchTerms }
      : (result?.v1 || currentV1),
  };
  if (textChanged) {
    Object.assign(patch, {
      complianceResult: null,
      scoringReport: null,
      revisionSuggestions: [],
      revisionSuggestionsError: '',
    });
  }
  setStateBulk?.(patch);
  if (textChanged) scheduleAutoSave?.(jobId);
  return {
    applied: textChanged,
    status: text(response?.status || response?.retry?.status),
    searchTerms: nextSearchTerms,
  };
}

